import { useEffect, useRef, useState } from 'react';

const VALUES = [
  { icon: 'ri-shield-check-line', title: '책임 시공', desc: '모든 공사는 시작부터 마감까지 담당 팀장이 직접 책임 관리하며, 완공 후 하자 발생 시 즉시 조치합니다.' },
  { icon: 'ri-leaf-line', title: '친환경 소재', desc: '새집증후군을 유발하는 유해물질이 없는 친환경 인증 도료만을 사용하여 가족 건강을 최우선으로 합니다.' },
  { icon: 'ri-customer-service-2-line', title: '투명한 소통', desc: '견적부터 시공, 사후 관리까지 모든 과정을 고객에게 투명하게 공유하고 소통합니다.' },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-20 md:py-28 bg-stone-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Top Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16 md:mb-24">
          {/* Left Text */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <span className="text-blue-400 text-sm font-bold tracking-widest uppercase">About Us</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6 leading-tight">
              라인 인테리어는<br />
              <span className="text-blue-400">신뢰로 완성</span>하는<br />
              시공 파트너입니다
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              라인인테리어는 주거·상업 공간의 페인트, 도장, 방수 전문 업체로 꾸준히 성장해 왔습니다.
              단순히 칠하는 것이 아니라, 공간의 가치를 높이고 오래가는 마감을 만드는 것이 라인 인테리어의 사명입니다.
              모든 현장에 숙련된 기술자가 직접 투입되며, 공사 전 과정을 고객과 함께 소통하며 진행합니다.
            </p>
          </div>

          {/* Right Image */}
          <div
            className={`relative transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="rounded-xl overflow-hidden w-full h-72 md:h-96">
              <img
                src="https://public.readdy.ai/ai/img_res/cf506d9795286b329ad3f09b61840ca1.jpg"
                alt="라인 인테리어 페인트 소재"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div
          className={`transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-2xl font-bold mb-8 text-white/90">우리의 핵심 가치</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {VALUES.map((val, idx) => (
              <div
                key={val.title}
                className={`p-5 rounded-xl bg-white/5 border border-white/10 transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + idx * 100}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600/20 mb-4">
                  <i className={`${val.icon} text-blue-400 text-lg`} />
                </div>
                <h4 className="font-bold text-white text-base mb-2">{val.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
